import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import {
  usePatchLoanAgencyFieldsMutation,
  usePatchLoanLiabilityLinesMutation,
} from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import type {
  LoanApplication,
  LoanLiabilityLine,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { MoneyField } from './MoneyField';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const SUBJECT_MORTGAGE = 'subject_mortgage';

const emptyRow = (): LoanLiabilityLine => ({
  creditorName: '',
  accountType: '',
  monthlyPayment: null,
  unpaidBalance: null,
  toBePaidOff: false,
});

const isBlankRow = (r: LoanLiabilityLine) =>
  !r.creditorName?.trim() &&
  !r.accountType?.trim() &&
  r.monthlyPayment == null &&
  r.unpaidBalance == null;

const usd0 = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

export function LiabilitiesSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanLiabilityLinesMutation();
  const [patchAgency] = usePatchLoanAgencyFieldsMutation();
  const { data: user } = useGetCurrentUserQuery();
  const [noDebts, setNoDebts] = useState(
    Boolean(application.agencyFields?.noOtherDebts)
  );
  const profileMonthlyDebt = user?.loanInfo?.monthlyDebt;

  const initialLines = useMemo(() => {
    const src = application.liabilityLines;
    const rows = src?.length ? src.map((r) => ({ ...r })) : [];
    // Always leave an empty row to type into, after the linked mortgage.
    if (!rows.some((r) => r.source !== SUBJECT_MORTGAGE)) rows.push(emptyRow());
    return rows;
  }, [application._id, application.liabilityLines, application.updatedAt]);

  const {
    draft: lines,
    setDraft: setLines,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'liabilities',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialLines,
    readOnly,
  });

  const otherLines = lines.filter(
    (r) => r.source !== SUBJECT_MORTGAGE && !isBlankRow(r)
  );

  const saveSection = async () => {
    if (formReadOnly) return;
    // Blank placeholder rows aren't debts.
    const toSave = lines.filter((r) => !isBlankRow(r));
    try {
      await patch({
        id: application._id,
        liabilityLines: toSave,
      }).unwrap();
      const confirmedNone = noDebts && otherLines.length === 0;
      if (confirmedNone !== Boolean(application.agencyFields?.noOtherDebts)) {
        await patchAgency({
          id: application._id,
          noOtherDebts: confirmedNone,
        }).unwrap();
      }
      markSaved(lines);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;
  const updateRow = (idx: number, patchRow: Partial<LoanLiabilityLine>) =>
    setLines((R) => R.map((r, i) => (i === idx ? { ...r, ...patchRow } : r)));

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Monthly debts that show on your credit report — car loans, student
        loans, credit cards, other mortgages. These update the payment estimates
        on your properties.
      </Typography>
      {profileMonthlyDebt && otherLines.length === 0 && !noDebts ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your pre-qualification listed about {usd0(profileMonthlyDebt)} a month
          in debts. List them here so the numbers match.
        </Alert>
      ) : null}
      {lines.map((row, idx) => {
        const linked = row.source === SUBJECT_MORTGAGE;
        const rowDisabled = disabled || linked;
        return (
          <Box
            key={row._id ?? idx}
            sx={{
              mb: 2,
              p: linked ? 1.5 : 0,
              borderRadius: 1.5,
              bgcolor: linked ? 'action.hover' : undefined,
            }}
          >
            {linked && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip size="small" label="Being refinanced" color="primary" />
                <Typography variant="caption" color="text.secondary">
                  Comes from “Current mortgage” — edit it there. It’s paid off
                  by the new loan, so it isn’t counted in your monthly debts.
                </Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Creditor"
                  value={row.creditorName}
                  disabled={rowDisabled}
                  onChange={(e) =>
                    updateRow(idx, { creditorName: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Type"
                  value={row.accountType}
                  disabled={rowDisabled}
                  onChange={(e) =>
                    updateRow(idx, { accountType: e.target.value })
                  }
                  placeholder="Auto loan, card, student…"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <MoneyField
                  label="Monthly payment"
                  value={row.monthlyPayment}
                  disabled={rowDisabled}
                  onChange={(v) => updateRow(idx, { monthlyPayment: v })}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <MoneyField
                  label="Balance"
                  value={row.unpaidBalance}
                  disabled={rowDisabled}
                  onChange={(v) => updateRow(idx, { unpaidBalance: v })}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={1}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {!linked && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={row.toBePaidOff}
                        disabled={rowDisabled}
                        onChange={(_, v) => updateRow(idx, { toBePaidOff: v })}
                      />
                    }
                    label="Pay off"
                  />
                )}
                {!formReadOnly && !linked && (
                  <IconButton
                    aria-label="Remove debt"
                    onClick={() =>
                      setLines((R) => R.filter((_, i) => i !== idx))
                    }
                  >
                    <i className="fa-light fa-trash" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        );
      })}
      {!formReadOnly && (
        <Box
          sx={{
            mb: 1,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setNoDebts(false);
              setLines((R) => [...R, emptyRow()]);
            }}
          >
            Add a debt
          </Button>
          {otherLines.length === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={noDebts}
                  disabled={disabled}
                  onChange={(_, v) => setNoDebts(v)}
                />
              }
              label="I don’t have any other debts"
            />
          )}
        </Box>
      )}
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={
          isDirty || noDebts !== Boolean(application.agencyFields?.noOtherDebts)
        }
        isSaving={isLoading}
        saveLabel="Save debts"
        onSave={() => void saveSection()}
        onCancel={() => {
          resetDraft();
          setNoDebts(Boolean(application.agencyFields?.noOtherDebts));
        }}
      />
    </div>
  );
}
